import React, { useEffect, useState } from 'react';
import './welcome-anim.css';
import { useNavigate } from 'react-router-dom';
import welcome from '../../../assets/welcome.png';
import img1 from '../../../assets/ld1.webp';
import img2 from '../../../assets/ld2.png';
import img3 from '../../../assets/ld3.png';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import LoginPage from '../../auth/screens/LoginPage';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import { authService } from '../../../services/authService';

/**
 * Helpdesk address in the [at]/[dot] form the Guidelines for Indian Government
 * Websites (GIGW) prescribe (security audit: Email Address Disclosure).
 *
 * The first fix assembled the address at runtime, which kept it out of the
 * served HTML and the JS bundle. But the page still rendered it as a plain
 * address with a mailto: link, so it sat in the live DOM, readable by any
 * harvester that runs scripts, and the Level 3 retest flagged it. In this form
 * a person can still read and type it, while nothing on the page, in the DOM
 * or in the bundle matches a name@domain pattern. There is deliberately no
 * mailto: link, because its href would put the address back.
 */
const HELPDESK_EMAIL_DISPLAY = 'samagamhelpdesk[at]kvs[dot]gov[dot]in';

export default function LandingScreen() {
  const [showLogin, setShowLogin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleLogin = () => {
    setShowLogin(false);
    navigate('/dashboard');
  };

  // Carousel slides (you can replace with actual images)
  const carouselImages = [
    img1,
    img2,
    img3
  ];

  if (showLogin) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f8f9fa' }}>
        <Header />
        <main className="flex-grow flex flex-col items-center justify-center w-full">
          <LoginPage onLogin={handleLogin} />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col gap-0" style={{ backgroundColor: '#fff' }}>
      <Header />
      <main className="flex-grow w-full">
        {/* Hero Section - Full Width Image Carousel */}
        <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

        {/* Hero Title Section */}
        <section className="w-full py-12 md:py-16 px-4 md:px-8 bg-gradient-to-br from-purple-50 to-white">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-4" style={{ color: '#6A1786' }}>
              VIDYA SAMIKSHA KENDRA
            </h1>
            <p className="text-lg md:text-xl text-gray-700 font-medium max-w-4xl mx-auto">
              VSK is not just a data system—it's a <span className="font-bold" style={{ color: '#6A1786' }}>"Force Multiplier"</span> for the entire KVS network.
            </p>
            <button
              onClick={() => setShowLogin(true)}
              className="mt-6 px-8 md:px-10 py-3 md:py-4 rounded-lg text-white font-semibold transition-all duration-300 hover:shadow-xl text-base md:text-lg"
              style={{ background: '#6A1786' }}
            >
              Login to VSK
            </button>
          </div>
        </section>

        {/* About VSK Section */}
        <section className="w-full py-12 md:py-16 px-4 md:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left: School Image with Rectangle Border */}
              <div className="relative flex items-center justify-center mb-8 lg:mb-0">
                <div
                  className="absolute welcome-rectangle-anim hidden lg:block"
                  style={{
                    width: '90%',
                    maxWidth: 360,
                    height: 'auto',
                    aspectRatio: '360/424',
                    border: '20px solid #6A1786',
                    zIndex: 1,
                    background: 'transparent',
                    left: '0',
                    top: '50%',
                    transform: 'translateY(-50%)'
                  }}
                />
                <img
                  src={welcome}
                  alt="Vidya Samiksha Kendra School"
                  className="relative z-10 w-full max-w-md lg:max-w-lg object-cover lg:ml-5"
                  style={{
                    boxShadow: '0 4px 16px 0 rgba(106,23,134,0.15)',
                  }}
                />
              </div>

              {/* Right: About VSK Text */}
              <div className="w-full">
                <h3 className="text-xs md:text-sm font-semibold mb-2" style={{ color: '#f4c430' }}>WHY WE NEED VSK</h3>
                <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6" style={{ color: '#6A1786' }}>
                  About KVS VSK
                </h2>
                <div className="mb-6 space-y-3">
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                    The Vidya Samiksha Kendra (VSK) is a visionary initiative under the <strong>National Education Policy (NEP) 2020</strong> that transforms 
                    how KVS monitors, analyses, and improves learning outcomes. KVS manages <strong>1290+ school systems</strong> in the world — spread across 
                    regions, languages, and communities. Traditional reporting models often operate in silos, making it difficult to track 
                    real-time progress.
                  </p>
                  <ul className="space-y-2 text-sm md:text-base text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="font-bold" style={{ color: '#6A1786' }}>1.</span>
                      <span>VSK bridges the data gap by bringing all educational information under one unified digital platform.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold" style={{ color: '#6A1786' }}>2.</span>
                      <span>Integrates multiple data sources such as KVS SAMAGAM, UDISE+, PM SHRI, DIKSHA and NISHTHA.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold" style={{ color: '#6A1786' }}>3.</span>
                      <span>Enables evidence-based decision making through real-time data insights.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold" style={{ color: '#6A1786' }}>4.</span>
                      <span>Ensures faster and more informed action to improve learning outcomes.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* VSK Platform Details */}
        <section className="w-full py-12 md:py-16 px-4 md:px-8" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-4xl font-bold mb-4" style={{ color: '#6A1786' }}>
                KVS VSK Platform
              </h2>
              <p className="text-base md:text-lg text-gray-700 max-w-4xl mx-auto">
                The KVS VSK is a centralized, data-driven command and control platform designed to:
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-3">📊</div>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#6A1786' }}>Monitor Performance</h3>
                <p className="text-gray-600 text-sm">Track school performance in real time across all regions</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-3">🎯</div>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#6A1786' }}>Analyze Indicators</h3>
                <p className="text-gray-600 text-sm">Key indicators for learning, attendance, and resources</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-3">💡</div>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#6A1786' }}>Actionable Insights</h3>
                <p className="text-gray-600 text-sm">Empower administrators, teachers, and policymakers</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md">
              <ul className="space-y-3 text-sm md:text-base text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="font-bold text-xl" style={{ color: '#6A1786' }}>•</span>
                  <span>It follows the NDEAR-aligned principles of interoperability, transparency, and continuous improvement.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="font-bold text-xl" style={{ color: '#6A1786' }}>•</span>
                  <span>By using advanced analytics and visualization dashboards, VSK ensures that every piece of data leads to meaningful action — from classroom interventions to national-level planning.</span>
                </li>
              </ul>
              <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: '#f3e8f7' }}>
                <p className="text-center text-base md:text-lg font-semibold" style={{ color: '#6A1786' }}>
                  "See, Understand, Act, Solve" — the continuous VSK cycle that keeps KVS responsive and future-ready.
                </p>
                <p className="text-center text-sm md:text-base text-gray-700 mt-2">
                  Real-time insights. Better learning for every child.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Impact at a Glance */}
        <section className="w-full py-12 md:py-16 px-4 md:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-4xl font-bold mb-4" style={{ color: '#6A1786' }}>
                Impact at a Glance
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border-2 border-purple-100 hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-3">👥</div>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#6A1786' }}>Attendance Tracking</h3>
                <p className="text-gray-600 text-sm">To track staff and student attendance in real-time</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border-2 border-purple-100 hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-3">🏛️</div>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#6A1786' }}>Strengthened Governance</h3>
                <p className="text-gray-600 text-sm">Data-driven governance across all levels</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border-2 border-purple-100 hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-3">⚡</div>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#6A1786' }}>Faster Decision-Making</h3>
                <p className="text-gray-600 text-sm">Quick intervention at all organizational levels</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border-2 border-purple-100 hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-3">👨‍🏫</div>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#6A1786' }}>Empowered Educators</h3>
                <p className="text-gray-600 text-sm">Empowered educators and administrators</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border-2 border-purple-100 hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-3">🔗</div>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#6A1786' }}>Unified System</h3>
                <p className="text-gray-600 text-sm">One unified, transparent system for KVS</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border-2 border-purple-100 hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-3">📈</div>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#6A1786' }}>Improved Outcomes</h3>
                <p className="text-gray-600 text-sm">Better learning outcomes for every child</p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Advantages */}
        <section className="w-full py-12 md:py-16 px-4 md:px-8" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-4xl font-bold mb-4" style={{ color: '#6A1786' }}>
                Empowering Education Through Data
              </h2>
              <p className="text-base md:text-lg text-gray-700">Our Advantages</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#6A1786' }}>1</div>
                  <h3 className="font-bold text-lg" style={{ color: '#6A1786' }}>Real-Time Monitoring</h3>
                </div>
                <p className="text-gray-600 text-sm">View live dashboards of student progress, attendance, and resource use</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#6A1786' }}>2</div>
                  <h3 className="font-bold text-lg" style={{ color: '#6A1786' }}>Integrated Systems</h3>
                </div>
                <p className="text-gray-600 text-sm">Brings together multiple education platforms for one unified view</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#6A1786' }}>3</div>
                  <h3 className="font-bold text-lg" style={{ color: '#6A1786' }}>Actionable Insights</h3>
                </div>
                <p className="text-gray-600 text-sm">Converts raw data into meaningful guidance for policy and classroom improvement</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#6A1786' }}>4</div>
                  <h3 className="font-bold text-lg" style={{ color: '#6A1786' }}>Teacher Empowerment</h3>
                </div>
                <p className="text-gray-600 text-sm">Enables teachers to identify learning gaps and personalize interventions</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#6A1786' }}>5</div>
                  <h3 className="font-bold text-lg" style={{ color: '#6A1786' }}>Efficient Governance</h3>
                </div>
                <p className="text-gray-600 text-sm">Reduces manual reporting, ensures transparency, and enhances accountability</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: '#6A1786' }}>6</div>
                  <h3 className="font-bold text-lg" style={{ color: '#6A1786' }}>Future-Ready Infrastructure</h3>
                </div>
                <p className="text-gray-600 text-sm">Built on open-source, scalable, and sustainable digital architecture</p>
              </div>
            </div>
          </div>
        </section>

        {/* Way Ahead */}
        <section className="w-full py-12 md:py-16 px-4 md:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-4xl font-bold mb-4" style={{ color: '#6A1786' }}>
                Way Ahead
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-purple-600 to-purple-800 p-8 rounded-xl text-white text-center hover:scale-105 transition-transform">
                <div className="text-5xl mb-4">🎯</div>
                <h3 className="font-bold text-xl mb-3">Learning Outcomes</h3>
                <p className="text-sm opacity-90">Measuring learning outcomes through targeted actions</p>
              </div>
              <div className="bg-gradient-to-br from-purple-600 to-purple-800 p-8 rounded-xl text-white text-center hover:scale-105 transition-transform">
                <div className="text-5xl mb-4">🤖</div>
                <h3 className="font-bold text-xl mb-3">AI Integration</h3>
                <p className="text-sm opacity-90">Integration of AI for predictive analysis</p>
              </div>
              <div className="bg-gradient-to-br from-purple-600 to-purple-800 p-8 rounded-xl text-white text-center hover:scale-105 transition-transform">
                <div className="text-5xl mb-4">🚀</div>
                <h3 className="font-bold text-xl mb-3">Interventions</h3>
                <p className="text-sm opacity-90">Providing intervention for improved learning outcomes</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="w-full py-12 md:py-16 px-4 md:px-8" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-4xl font-bold mb-4" style={{ color: '#6A1786' }}>
                Get in Touch
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-md">
                <h3 className="font-bold text-xl mb-4" style={{ color: '#6A1786' }}>Address</h3>
                <p className="text-gray-700 leading-relaxed">
                  Kendriya Vidyalaya Sangathan (HQ)<br />
                  18, Institutional Area,<br />
                  Shaheed Jeet Singh Marg,<br />
                  New Delhi – 110016
                </p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-md">
                <h3 className="font-bold text-xl mb-4" style={{ color: '#6A1786' }}>Contact Details</h3>
                <div className="space-y-3 text-gray-700">
                  <p className="flex items-center gap-2">
                    <span className="font-semibold">📞 Phone:</span>
                    <a href="tel:+91 11-26523069" className="hover:text-purple-600">+91-11-26523069</a>
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="font-semibold">📧 Email:</span>
                    <span>{HELPDESK_EMAIL_DISPLAY}</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="font-semibold">🌐 Website:</span>
                    <a href="https://www.kvsangathan.nic.in" target="_blank" rel="noopener noreferrer" className="hover:text-purple-600">www.kvs.gov.in</a>
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="font-semibold">🌐 VSK Portal:</span>
                    <a href="https://vsk.kvs.gov.in" target="_blank" rel="noopener noreferrer" className="hover:text-purple-600">samiksha.kvs.gov.in</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}









